import React, { useState, useEffect } from "react";
import { api, useAxiosInterceptor } from "../shared/axiosInstance";
import { useNavigate } from "react-router-dom";
import { captureUtmToSessionStorage, readUtmFromSessionStorage } from "../../utils/utmStorage";
import { postcodeValidator } from "postcode-validator";
import countries from "i18n-iso-countries";

// Register the English locale for country validation
countries.registerLocale(require("i18n-iso-countries/langs/en.json"));

const EmployerSignUp = () => {
    useAxiosInterceptor();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        companyName: "",
        companyWebsite: "",
        postalCode: "",
        country: "",
        careerSatisfaction: "",
        status: "active",
    });

    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");

    // Capture UTM params from URL on component mount (first-touch only)
    useEffect(() => {
        captureUtmToSessionStorage();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        // Only allow letters for the country field
        if (name === "country") {
            const filteredValue = value.replace(/[^A-Za-z]/g, "");
            setFormData({ ...formData, [name]: filteredValue });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        // Validate the two-letter country code (ISO 3166-1 alpha-2)
        const countryCode = formData.country.trim().toUpperCase();
        if (!countries.isValid(countryCode)) {
            setError("Please enter a valid two-letter country code (e.g. US, MX, CA).");
            return;
        }

        // Validate the postal code for the given country
        if (!postcodeValidator(formData.postalCode, countryCode)) {
            setError("Please enter a valid postal code for the given country.");
            return;
        }

        try {
            // Merge UTM attribution from sessionStorage into signup payload
            const utmData = readUtmFromSessionStorage() || {};
            const payload = { ...formData, ...utmData };
            const res = await api.post("/auth/employer-signup", payload);
            if (res.data.pending) {
                setSuccessMessage(res.data.message);
            } else {
                navigate("/signin");
            }
        } catch (error) {
            setError(error.response?.data?.message || error.response?.data?.error || "Something went wrong!");
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-darkblue text-lightgray relative">
            <div className="w-full max-w-md bg-bluegray shadow-lg rounded-lg p-6">
                <h2 className="text-2xl font-bold text-center text-orange mb-4">Employer Signup</h2>
                {error && <p className="text-red text-center mb-4">{error}</p>}
                {successMessage && (
                    <div className="border-l-4 border-yellow-500 bg-deepblue rounded-md p-4 mb-4">
                        <p className="text-yellow-400 font-semibold mb-1">Account Created — Pending Approval</p>
                        <p className="text-sm text-lightgray mb-2">
                            Your account has been created but access to the company dashboard requires approval from the existing company admin. They have been notified.
                        </p>
                        <p className="text-sm text-lightgray">Please check your email to verify your account. You will receive another email once approved.</p>
                        <button
                            type="button"
                            onClick={() => navigate("/signin")}
                            className="mt-3 w-full bg-orange text-darkblue py-2 rounded font-bold hover:bg-lightorange transition"
                        >
                            Go to Sign In
                        </button>
                    </div>
                )}
                <form onSubmit={handleSubmit} className={`space-y-4 ${successMessage ? 'hidden' : ''}`}>
                    <div>
                        <label className="block text-sm font-medium mb-1">First Name</label>
                        <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            placeholder="Enter first name"
                            required
                            autoComplete="given-name"
                            className="w-full px-3 py-2 rounded bg-deepblue text-lightgray border border-lightblue focus:ring-orange"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Last Name</label>
                        <input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            placeholder="Enter last name"
                            required
                            autoComplete="family-name"
                            className="w-full px-3 py-2 rounded bg-deepblue text-lightgray border border-lightblue focus:ring-orange"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Email</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Enter email"
                            required
                            autoComplete="email"
                            className="w-full px-3 py-2 rounded bg-deepblue text-lightgray border border-lightblue focus:ring-orange"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Password</label>
                        <p className="text-xs text-gray-400 mb-1">
                            Minimum 12 characters. <a href="https://xkcd.com/936" target="_blank" rel="noopener noreferrer" className="text-lightblue underline">Tips</a>
                        </p>
                        <input
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder="Min 12 characters"
                            required
                            minLength={12}
                            autoComplete="new-password"
                            className="w-full px-3 py-2 rounded bg-deepblue text-lightgray border border-lightblue focus:ring-orange"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Company Name</label>
                        <input
                            type="text"
                            name="companyName"
                            value={formData.companyName}
                            onChange={handleChange}
                            placeholder="Enter company name"
                            required
                            autoComplete="organization"
                            className="w-full px-3 py-2 rounded bg-deepblue text-lightgray border border-lightblue focus:ring-orange"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Company Website</label>
                        <input
                            type="url"
                            name="companyWebsite"
                            value={formData.companyWebsite}
                            onChange={handleChange}
                            onBlur={(e) => {
                                const val = e.target.value.trim();
                                if (val && !/^https?:\/\//i.test(val)) {
                                    setFormData({ ...formData, companyWebsite: 'https://' + val });
                                }
                            }}
                            placeholder="yourcompany.com"
                            autoComplete="url"
                            className="w-full px-3 py-2 rounded bg-deepblue text-lightgray border border-lightblue focus:ring-orange"
                        />
                        <p className="text-xs text-gray-400 mt-1">Optional for personal email addresses</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Postal Code</label>
                        <input
                            type="text"
                            name="postalCode"
                            value={formData.postalCode}
                            onChange={handleChange}
                            placeholder="Enter postal code"
                            required
                            autoComplete="postal-code"
                            className="w-full px-3 py-2 rounded bg-deepblue text-lightgray border border-lightblue focus:ring-orange"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Country (e.g. US, MX)</label>
                        <input
                            type="text"
                            name="country"
                            value={formData.country}
                            onChange={handleChange}
                            placeholder="US"
                            required
                            autoComplete="country"
                            maxLength={2}
                            className="w-full px-3 py-2 rounded bg-deepblue text-lightgray border border-lightblue focus:ring-orange"
                        />
                    </div>
                    <button type="submit" className="w-full py-2 bg-orange text-white font-medium rounded-lg hover:bg-deeporange">
                        Sign Up
                    </button>
                </form>
            </div>
        </div>
    );
};

export default EmployerSignUp;
